import { useEffect, useRef, useState } from "react";

// Redux.
import { connect } from 'react-redux';
import {
    preloadTopicPreferences,
    setPodcastSubscribeStatus,
    setSpeakerSubscribeStatus,
    setTopicPreferenceStatus,
} from '../redux/actions/userActions';

// Components.
import { fancyTimeFormatMinutesOrSeconds, formatPreviewInfoPublishedDate } from './timeHelper';
import usePreviewBottomVisible from './usePreviewBottomVisible';

import { MoreHorizRounded } from '@mui/icons-material';
import { useNavigate } from "react-router-dom";
import SpeakerRow from "./SpeakerRow";
import PreviewImage from "./PreviewImage";
import PreviewPlayButton from "./PreviewPlayButton";
import PreviewBookmarkButton from "./PreviewBookmarkButton";
import PreviewInfoOptionsDrawer from "./PreviewInfoOptionsDrawer";
import PreviewMoreOrLessDrawer from "./PreviewMoreOrLessDrawer";
import { getChatPreviewHeading } from '../utils/chatPreviewPresentation';
import { METADATA_IMAGE_TEXT_SPACING } from '../constants/spacing';
import './PreviewInfo.css';

const NOOP = () => {};
const RESOLVED_NOOP = () => Promise.resolve();


export const PreviewInfo = ({
    preview,
    generatedHeading,
    previewPresentationMode,
    index,
    indexInViewport = NOOP,
    playPreview,
    onImageClick = playPreview,
    onBodyClick = playPreview,
    onSourceClick,
    onTopicPlay = playPreview,
    topicActionLabel,
    onSpeakerClick,
    actionLabel,
    scrollToId,
    imageBorderRadius = '16px',
    horizontalPadding = 20,
    speakerRowMarginBottom = 8,
    bodyPaddingTop = 8,
    bodyPaddingBottom = 0,
    className = '',
    speakerRowClassName = '',
    mediaClassName = '',
    bodyClassName = '',
    bodyButtonClassName = '',
    titleClassName = '',
    metaClassName = '',
    showFooterControls = true,
    showOptionsMenu = true,
    showTopicLabel = true,
    disableShareLink = false,
    disablePlayback = false,
    disableFullEpisode = false,
    onFullEpisodeClick,
    onReportClick,

    // Redux.
    authenticated,
    speakerSubscribeIds,
    speakerSubscribeStatusById,
    podcastSubscribeIds,
    podcastSubscribeStatusById,
    topicPreferenceStatusById = {},
    topicPreferencesLoaded = false,
    topicPreferencesLoading = false,
    topicPreferencesError = null,

    // Redux actions.
    setSpeakerSubscribeStatus,
    setPodcastSubscribeStatus,
    preloadTopicPreferences = NOOP,
    setTopicPreferenceStatus = RESOLVED_NOOP,
}) => {

    // The response protocol retains this mode name; chat now uses the preview card.
    const chatPresentation = previewPresentationMode === 'summary_only';
    const bottomViewport = useRef();
    const isCurrentViewport = usePreviewBottomVisible(bottomViewport, preview.id);

    useEffect(() => {
        if (isCurrentViewport) {
            indexInViewport(index);
        }
    }, [index, indexInViewport, isCurrentViewport]);

    const navigate = useNavigate();
    const [showMoreOrLess, setShowMoreOrLess] = useState(false);
    const [showPreviewOptions, setShowPreviewOptions] = useState(false);

    useEffect(() => {
        if (authenticated) {
            preloadTopicPreferences();
        }
    }, [authenticated, preloadTopicPreferences]);

    const openMoreOrLess = () => {
        preloadTopicPreferences();
        setShowMoreOrLess(true);
    };

    const sourceId = preview.source?.id;
    const openSource = () => {
        if (!sourceId) return;
        if (onSourceClick) {
            onSourceClick(sourceId);
        } else {
            navigate(`/podcast/${sourceId}`);
        }
    };

    const heading = chatPresentation ? getChatPreviewHeading(generatedHeading) : null;
    const displayTitle = heading || preview.title;
    const publishedDateText = formatPreviewInfoPublishedDate(preview.publishedDate);
    const durationText = fancyTimeFormatMinutesOrSeconds(preview.totalTimeSeconds);
    const speakerName = typeof preview.person?.name === 'string' ? preview.person.name.trim() : '';
    const playLabel = actionLabel || (chatPresentation ?
        `Play ${durationText} preview${speakerName ? ` by ${speakerName}` : ''}. ${displayTitle || 'Highlight'}` :
        `Play ${durationText} preview`);

    const handleCardClick = (event) => {
        // Controls keep their own actions, and drawer portals are outside the card.
        if (event.defaultPrevented || !event.currentTarget.contains(event.target)
            || event.target.closest('button, a, input, select, textarea, [role="button"], [role="link"]')) return;
        if (!disablePlayback) onImageClick?.(event);
    };

    const speakerRow = (
        <div
            className={['previewInfoSpeakerRow', speakerRowClassName].filter(Boolean).join(' ')}
            style={{ margin: `0px ${horizontalPadding}px ${speakerRowMarginBottom}px` }}>
            <SpeakerRow
                speaker={preview.person || { name: 'Speaker unavailable' }}
                variant='metadata'
                imageTextSpacing={METADATA_IMAGE_TEXT_SPACING}
                onSpeakerClick={onSpeakerClick}
            />
        </div>
    );
    const moreButton = showOptionsMenu && (
        <button type='button' className='previewInfoFooterIconButton' aria-label='More options' onClick={() => setShowPreviewOptions(true)}>
            <MoreHorizRounded className='previewInfoFooterIcon' />
        </button>
    );
    const renderCaption = (hasPreviewImage) => {
        const titleButton = hasPreviewImage && <button type='button' onClick={onBodyClick} disabled={disablePlayback}
            aria-label={chatPresentation ? playLabel : `${playLabel}. ${displayTitle || 'Highlight'}`}
            className={['previewInfoBodyButton', bodyButtonClassName].filter(Boolean).join(' ')}
            style={{ textAlign: 'left', width: '100%' }}>
            <div className={['previewInfoTitle', titleClassName].filter(Boolean).join(' ')}>{displayTitle}</div>
        </button>;
        return <div
            className={['previewInfoBody', 'previewInfoCaption', !hasPreviewImage ? 'previewInfoCaptionWithoutTitle' : '', bodyClassName].filter(Boolean).join(' ')}
            style={{ padding: `${hasPreviewImage ? bodyPaddingTop : 0}px 0px ${bodyPaddingBottom}px` }}>
            <div className='previewInfoCaptionText'>
                {titleButton}
                <div className='previewInfoCaptionMeta'>
                    <PreviewPlayButton
                        onClick={showFooterControls ? playPreview : onImageClick}
                        label={playLabel}
                        duration={durationText}
                        disabled={disablePlayback}
                        compact
                    />
                    {publishedDateText && <span className='previewInfoDateGroup'>
                        <span aria-hidden='true'>·</span>
                        <span className={['previewInfoDate', metaClassName].filter(Boolean).join(' ')}>{publishedDateText}</span>
                    </span>}
                </div>
            </div>
            {showFooterControls && <div className='previewInfoSaveActions'><PreviewBookmarkButton preview={preview} />{moreButton}</div>}
        </div>;
    };

    return (
        <div id={scrollToId}
            className={['previewInfoContainer', 'previewInfoCard', 'previewInfoHighlightCard', className].filter(Boolean).join(' ')}
            onClick={handleCardClick}
            style={{
                textAlign: 'left',
            }}>
            {speakerRow}

            <div
                className={['previewInfoMedia', mediaClassName].filter(Boolean).join(' ')}
                style={{ position: 'relative', padding: `0px ${horizontalPadding}px` }}>
                <PreviewImage
                    preview={preview}
                    displayTitle={displayTitle}
                    onImageClick={onImageClick}
                    imageDisabled={disablePlayback}
                    onTopicClick={disablePlayback ? undefined : showFooterControls ? onTopicPlay : onImageClick}
                    topicActionLabel={showFooterControls ? topicActionLabel || playLabel : playLabel}
                    topicLabel={showTopicLabel ? undefined : ''}
                    imageActionLabel={playLabel}
                    onSourceClick={openSource}
                    imageBorderRadius={imageBorderRadius}
                    highlightLayout
                    titleClassName={titleClassName}
                    renderCaption={renderCaption}
                />
            </div>

            <div ref={bottomViewport} className='previewInfoBottomVisibility' aria-hidden='true' />

            {showFooterControls && showOptionsMenu && (
                <>
                    <PreviewMoreOrLessDrawer
                        open={showMoreOrLess}
                        onClose={() => setShowMoreOrLess(false)}
                        preview={preview}
                        authenticated={authenticated}
                        speakerSubscribeIds={speakerSubscribeIds}
                        speakerSubscribeStatusById={speakerSubscribeStatusById}
                        podcastSubscribeIds={podcastSubscribeIds}
                        podcastSubscribeStatusById={podcastSubscribeStatusById}
                        setSpeakerSubscribeStatus={setSpeakerSubscribeStatus}
                        setPodcastSubscribeStatus={setPodcastSubscribeStatus}
                        topicPreferenceStatusById={topicPreferenceStatusById}
                        topicPreferencesLoaded={topicPreferencesLoaded}
                        topicPreferencesLoading={topicPreferencesLoading}
                        topicPreferencesError={topicPreferencesError}
                        preloadTopicPreferences={preloadTopicPreferences}
                        setTopicPreferenceStatus={setTopicPreferenceStatus}
                    />
                    <PreviewInfoOptionsDrawer
                        open={showPreviewOptions}
                        onClose={() => setShowPreviewOptions(false)}
                        preview={preview}
                        authenticated={authenticated}
                        disableShareLink={disableShareLink}
                        disableFullEpisode={disableFullEpisode}
                        onFullEpisodeClick={onFullEpisodeClick}
                        onReportClick={onReportClick}
                        onMoreOrLessClick={openMoreOrLess}
                    />
                </>
            )}
        </div >
    );
};

const mapStateToProps = (state) => ({
    authenticated: state.user.authenticated,
    speakerSubscribeIds: state.user.speakerSubscribeIds,
    speakerSubscribeStatusById: state.user.speakerSubscribeStatusById,
    podcastSubscribeIds: state.user.podcastSubscribeIds,
    podcastSubscribeStatusById: state.user.podcastSubscribeStatusById,
    topicPreferenceStatusById: state.user.topicPreferenceStatusById,
    topicPreferencesLoaded: state.user.topicPreferencesLoaded,
    topicPreferencesLoading: state.user.topicPreferencesLoading,
    topicPreferencesError: state.user.topicPreferencesError,
})

const mapActionsToProps = {
    setSpeakerSubscribeStatus,
    setPodcastSubscribeStatus,
    preloadTopicPreferences,
    setTopicPreferenceStatus,
}

export default connect(mapStateToProps, mapActionsToProps)(PreviewInfo);
